import React, { useState, useEffect, useRef } from 'react';
import { getSocket } from '../utils/socket';
import { applyTheme, resolveTheme } from '../theme';
import { getBranchTheme } from '../utils/auth';
import './GamePlay.css';

function GamePlay({ sessionCode }) {
  const [socket, setSocket] = useState(null);
  const [playerName, setPlayerName] = useState('');
  const playerNameRef = useRef(''); // always-current ref to avoid stale closures
  const [savedName, setSavedName] = useState(''); // name loaded from localStorage only
  const [playerPin, setPlayerPin] = useState('');
  const [showPinPrompt, setShowPinPrompt] = useState(false);
  const [pinError, setPinError] = useState('');
  const [nameEntered, setNameEntered] = useState(false);
  const [gameState, setGameState] = useState('waiting'); // waiting, question, results, finished
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [participantCount, setParticipantCount] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [textAnswer, setTextAnswer] = useState('');
  const [answered, setAnswered] = useState(false);
  const [questionResult, setQuestionResult] = useState(null);
  const [myTotalScore, setMyTotalScore] = useState(0);
  const [myRank, setMyRank] = useState(0);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(0);

  // Keep ref in sync with playerName state (solves stale closure in socket handlers)
  useEffect(() => {
    playerNameRef.current = playerName;
  }, [playerName]);

  // Apply branch theme immediately (session theme overrides once join_success fires)
  useEffect(() => {
    applyTheme(resolveTheme(getBranchTheme(), null, null));
  }, []);

  // Check localStorage for saved session data and auto-rejoin with token
  useEffect(() => {
    const savedData = localStorage.getItem(`ctta_session_${sessionCode}`);
    if (savedData) {
      try {
        const { name, token } = JSON.parse(savedData);
        setPlayerName(name);
        setSavedName(name); // mark as a known returning player
        console.log('📱 Found saved session:', name);

        // If we have a token, try to auto-rejoin
        if (token) {
          console.log('🔑 Attempting auto-rejoin with token...');
          const socketInstance = getSocket();
          socketInstance.emit('rejoin_with_token', {
            sessionCode,
            name,
            token
          });
        }
      } catch (e) {
        console.error('Error parsing saved session data:', e);
      }
    }
  }, [sessionCode]);

  useEffect(() => {
    const socketInstance = getSocket();
    setSocket(socketInstance);

    // Listen for socket events
    socketInstance.on('join_success', (data) => {
      console.log('✅ Join/Rejoin success:', data);

      // Apply session theme (falls back to branch, then purple)
      applyTheme(resolveTheme(getBranchTheme(), null, data.theme || null));

      // Save to localStorage using ref (avoids stale closure - playerName state would be '' here)
      localStorage.setItem(`ctta_session_${sessionCode}`, JSON.stringify({
        name: playerNameRef.current,
        token: data.sessionToken,
        joinedAt: new Date().toISOString()
      }));

      setNameEntered(true);
      setGameState(data.currentState);
      setShowPinPrompt(false);

      // Show welcome back message if rejoined or auto-rejoined
      if (data.rejoined || data.autoRejoined) {
        console.log('🔄 Rejoined with score:', data.currentScore);
      }

      // Handle state-specific data when joining mid-game
      if (data.currentQuestion) {
        // Joining during active question
        setCurrentQuestion(data.currentQuestion);
        setTimeLeft(data.currentQuestion.timeRemaining || data.currentQuestion.timeLimit);
      } else if (data.questionResult) {
        // Joining during results display
        setQuestionResult(data.questionResult);
      } else if (data.finalLeaderboard) {
        // Joining during final leaderboard
        setQuestionResult({ leaderboard: data.finalLeaderboard });
      }
    });

    socketInstance.on('participant_joined', (data) => {
      setParticipantCount(data.participantCount);
    });

    socketInstance.on('show_welcome', (data) => {
      setGameState('waiting');
      setParticipantCount(data.participantCount);
      setCurrentQuestion(null);
      setAnswered(false);
    });

    socketInstance.on('question_countdown', (data) => {
      setGameState('countdown');
      setCountdown(data.seconds);
    });

    socketInstance.on('question_started', (data) => {
      console.log('🎯 QUESTION_STARTED event received:', data);
      setGameState('question');
      setCurrentQuestion(data);
      setTimeLeft(data.timeLimit);
      setSelectedAnswer(null);
      setTextAnswer('');
      setAnswered(false);
      setQuestionResult(null);
      setError('');
      setCountdown(0);
    });

    socketInstance.on('answer_submitted', (data) => {
      console.log('Answer submitted:', data);
      setAnswered(true);
      if (data.totalScore !== undefined) setMyTotalScore(data.totalScore);
    });

    socketInstance.on('question_ended', (data) => {
      console.log('📊 QUESTION_ENDED event received:', data);
      setGameState('results');
      setQuestionResult(data.result);
      // Persist rank so it shows on the waiting screen between questions
      const leaderboard = (data.result && data.result.cumulativeLeaderboard) || [];
      const rankIdx = leaderboard.findIndex(e => e.name === playerNameRef.current);
      if (rankIdx !== -1) setMyRank(rankIdx + 1);
      console.log('State updated to: results');
    });

    socketInstance.on('show_leaderboard', (data) => {
      setGameState('finished');
      setQuestionResult({ leaderboard: data.leaderboard });
    });

    socketInstance.on('error', (data) => {
      if (data.message === 'Name already taken') {
        console.log('🔒 Name exists, prompting for PIN');
        setShowPinPrompt(true);
        setError('');
      } else {
        setError(data.message);
      }
    });

    socketInstance.on('rejoin_error', (data) => {
      console.log('❌ Rejoin error:', data.message);
      if (data.message === 'Incorrect PIN') {
        setPinError('Incorrect PIN. This name is already taken.');
      } else {
        setPinError(data.message);
      }
    });

    socketInstance.on('token_invalid', (data) => {
      console.log('❌ Token invalid:', data.message);
      // Session gone (server restart / expiry) - clear everything for a fresh start
      localStorage.removeItem(`ctta_session_${sessionCode}`);
      setShowPinPrompt(false);
      setSavedName('');
      setPlayerName('');
      setPlayerPin('');
    });

    socketInstance.on('state_updated', (data) => {
      console.log('🔄 State updated:', data);
      setGameState(data.currentState);

      // Update state-specific data
      if (data.currentQuestion) {
        setCurrentQuestion(data.currentQuestion);
        setTimeLeft(data.currentQuestion.timeRemaining || data.currentQuestion.timeLimit);
        setAnswered(false);
        setSelectedAnswer(null);
        setTextAnswer('');
      } else if (data.questionResult) {
        setQuestionResult(data.questionResult);
      } else if (data.finalLeaderboard) {
        setQuestionResult({ leaderboard: data.finalLeaderboard });
      }
    });

    return () => {
      socketInstance.off('join_success');
      socketInstance.off('participant_joined');
      socketInstance.off('show_welcome');
      socketInstance.off('question_countdown');
      socketInstance.off('question_started');
      socketInstance.off('answer_submitted');
      socketInstance.off('question_ended');
      socketInstance.off('show_leaderboard');
      socketInstance.off('error');
      socketInstance.off('rejoin_error');
      socketInstance.off('token_invalid');
      socketInstance.off('state_updated');
    };
  }, []);

  // Log state changes
  useEffect(() => {
    console.log('🔄 GameState changed to:', gameState);
  }, [gameState]);

  // Timer countdown
  useEffect(() => {
    if (gameState === 'question' && timeLeft > 0 && !answered) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [gameState, timeLeft, answered]);

  // Countdown ticker
  useEffect(() => {
    if (gameState === 'countdown' && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [gameState, countdown]);

  // Auto-sync on visibility change (phone unlock, tab switch back)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && nameEntered && socket) {
        console.log('👁️ Page visible again, syncing state...');
        socket.emit('get_current_state', {
          sessionCode,
          name: playerName
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [nameEntered, sessionCode, playerName, socket]);

  const handleNameSubmit = (e) => {
    e.preventDefault();
    if (!playerName.trim()) {
      setError('Please enter your name');
      return;
    }

    if (!playerPin || playerPin.length !== 4) {
      setError('Please enter a 4-digit PIN');
      return;
    }

    setError('');
    socket.emit('join_game', {
      sessionCode,
      name: playerName.trim(),
      pin: playerPin
    });
  };

  const handlePinSubmit = (e) => {
    e.preventDefault();
    if (!playerPin || playerPin.length !== 4) {
      setPinError('Please enter a 4-digit PIN');
      return;
    }

    setPinError('');
    socket.emit('rejoin_game', {
      sessionCode,
      name: playerName.trim(),
      pin: playerPin
    });
  };

  const handleAnswerSubmit = () => {
    if (answered) return;

    let answer;
    if (currentQuestion.type === 'multiple_choice') {
      if (selectedAnswer === null) {
        setError('Please select an answer');
        return;
      }
      answer = selectedAnswer;
    } else {
      if (!textAnswer.trim()) {
        setError('Please enter your answer');
        return;
      }
      answer = textAnswer.trim();
    }

    setError('');
    socket.emit('submit_answer', {
      sessionCode,
      name: playerName,
      answer
    });
  };

  const handleRefreshGame = () => {
    console.log('🔄 Manual refresh requested');
    socket.emit('get_current_state', {
      sessionCode,
      name: playerName
    });
  };

  const handleClearSession = () => {
    localStorage.removeItem(`ctta_session_${sessionCode}`);
    setSavedName('');
    setPlayerName('');
    setPlayerPin('');
    setError('');
  };

  // Name entry screen
  if (!nameEntered) {
    // Show PIN prompt if name already taken
    if (showPinPrompt) {
      return (
        <div className="game-play-container">
          <div className="game-play-card">
            <h1>Welcome Back!</h1>
            <p className="subtitle">Enter your PIN to continue as {playerName}</p>

            <form onSubmit={handlePinSubmit} className="pin-form">
              <input
                type="number"
                className="pin-input"
                placeholder="4-digit PIN"
                value={playerPin}
                onChange={(e) => {
                  const value = e.target.value.slice(0, 4);
                  setPlayerPin(value);
                }}
                maxLength={4}
                pattern="[0-9]{4}"
                autoFocus
              />

              {pinError && <div className="error-message">{pinError}</div>}

              <div className="button-group">
                <button type="submit" className="submit-button">
                  Continue
                </button>
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => {
                    setShowPinPrompt(false);
                    setPlayerName('');
                    setPlayerPin('');
                    setPinError('');
                  }}
                >
                  Use Different Name
                </button>
              </div>
            </form>
          </div>
        </div>
      );
    }

    // Determine if this is a recognised returning player
    const isReturning = Boolean(savedName && playerName === savedName);

    // Regular name entry / rejoin form
    return (
      <div className="game-play-container">
        <div className="game-play-card">
          <h1>{isReturning ? 'Welcome Back!' : 'Join Game'}</h1>
          <p className="subtitle">Game Code: {sessionCode}</p>

          {isReturning && (
            <div className="saved-name-notice">
              Continuing as <strong>{savedName}</strong>
              <button type="button" className="link-button" onClick={handleClearSession}>
                Not you?
              </button>
            </div>
          )}

          <form onSubmit={handleNameSubmit} className="name-form">
            <input
              type="text"
              className="name-input"
              placeholder="Your Name"
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              maxLength={30}
              autoFocus={!isReturning}
            />

            <input
              type="number"
              className="pin-input"
              placeholder={isReturning ? 'Enter your 4-digit PIN' : 'Create a 4-digit PIN'}
              value={playerPin}
              onChange={(e) => setPlayerPin(e.target.value.slice(0, 4))}
              maxLength={4}
              pattern="[0-9]{4}"
              autoFocus={isReturning}
            />

            <p className="pin-hint">
              {isReturning
                ? '🔑 Enter the PIN you set when you first joined'
                : "💡 Remember this PIN — you'll need it to rejoin if disconnected"}
            </p>

            {error && <div className="error-message">{error}</div>}

            <button type="submit" className="submit-button">
              {isReturning ? 'Rejoin Game' : 'Join Game'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Waiting screen (both 'waiting' and 'welcome' states show this)
  if (gameState === 'waiting' || gameState === 'welcome') {
    const hasScore = myTotalScore > 0 || myRank > 0;
    return (
      <div className="game-play-container">
        <div className="game-play-card">
          <div className="waiting-icon">⏳</div>
          <h1>Waiting for Next Question...</h1>

          {hasScore ? (
            <div className="waiting-score-card">
              <div className="waiting-player-name">{playerName}</div>
              <div className="waiting-score-row">
                {myRank > 0 && (
                  <span className="waiting-rank">#{myRank}</span>
                )}
                <span className="waiting-score">{myTotalScore} pts</span>
              </div>
            </div>
          ) : (
            <p className="subtitle">Welcome, {playerName}!</p>
          )}

          <div className="participant-count">
            <span className="count-number">{participantCount}</span>
            <span className="count-label">Participants</span>
          </div>

          <div className="game-controls">
            <button onClick={handleRefreshGame} className="refresh-button">
              🔄 Refresh Game
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Countdown screen
  if (gameState === 'countdown') {
    return (
      <div className="game-play-container">
        <div className="game-play-card countdown-card">
          <p className="countdown-label">Get ready!</p>
          <div className="countdown-number">{countdown}</div>
        </div>
      </div>
    );
  }

  // Question screen
  if (gameState === 'question' && currentQuestion) {
    return (
      <div className="game-play-container">
        <div className="game-play-card">
          <div className="question-header">
            <div className="timer">
              <span className="timer-icon">⏱</span>
              <span className="timer-value">{timeLeft}s</span>
            </div>
            <button onClick={handleRefreshGame} className="refresh-button-small">
              🔄
            </button>
          </div>

          <h2 className="question-text">{currentQuestion.text}</h2>

          {currentQuestion.type === 'multiple_choice' ? (
            <div className="options-grid">
              {currentQuestion.options.map((option, index) => (
                <button
                  key={index}
                  className={`option-button ${selectedAnswer === index ? 'selected' : ''} ${answered ? 'disabled' : ''}`}
                  onClick={() => !answered && setSelectedAnswer(index)}
                  disabled={answered}
                >
                  {option}
                </button>
              ))}
            </div>
          ) : (
            <div className="text-answer-section">
              <textarea
                className="text-answer-input"
                placeholder="Type your answer here..."
                value={textAnswer}
                onChange={(e) => setTextAnswer(e.target.value)}
                disabled={answered}
                rows={4}
              />
            </div>
          )}

          {error && <div className="error-message">{error}</div>}

          {!answered ? (
            <button
              className="submit-answer-button"
              onClick={handleAnswerSubmit}
              disabled={timeLeft === 0}
            >
              Submit Answer
            </button>
          ) : (
            <div className="answered-message">
              ✓ Answer submitted! Waiting for others...
            </div>
          )}
        </div>
      </div>
    );
  }

  // Results screen
  if (gameState === 'results' && questionResult) {
    if (questionResult.type === 'multiple_choice' && Array.isArray(questionResult.options)) {
      const total = questionResult.responses ? questionResult.responses.length : 0;
      const counts = questionResult.answerCounts || [];
      const correct = questionResult.correctAnswer;
      const myRank = (questionResult.cumulativeLeaderboard || []).findIndex(e => e.name === playerName) + 1;

      return (
        <div className="game-play-container">
          <div className="game-play-card">
            <h1>Results</h1>

            {questionResult.questionText && (
              <p className="results-question-text">{questionResult.questionText}</p>
            )}

            <div className="answer-chart">
              {questionResult.options.map((option, index) => {
                const count = counts[index] || 0;
                const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                const isCorrect = index === correct;
                const myAnswer = index === selectedAnswer;

                return (
                  <div key={index} className={`chart-row ${isCorrect ? 'correct-row' : ''} ${myAnswer ? 'my-answer-row' : ''}`}>
                    <div className="chart-label">
                      <span className="option-letter">{String.fromCharCode(65 + index)}</span>
                      <span className="option-text">{option}</span>
                      {isCorrect && <span className="correct-mark">✓</span>}
                    </div>
                    <div className="chart-bar-wrap">
                      <div
                        className={`chart-bar ${isCorrect ? 'bar-correct' : 'bar-wrong'}`}
                        style={{ width: `${pct}%` }}
                      />
                      <span className="chart-count">{count} ({pct}%)</span>
                    </div>
                  </div>
                );
              })}
            </div>

            <p className="total-responses">{total} response{total !== 1 ? 's' : ''}</p>

            <div className="results-footer">
              <div className="my-score-banner">
                <span className="my-score-name">{playerName}</span>
                <span className="my-score-total">
                  {myRank > 0 ? `#${myRank} · ` : ''}{myTotalScore} pts
                </span>
              </div>
              <button onClick={handleRefreshGame} className="refresh-icon-button" title="Refresh Game">
                🔄
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Free text — word cloud shown on display screen
    return (
      <div className="game-play-container">
        <div className="game-play-card">
          <h1>Responses Submitted</h1>
          <div className="my-score-banner">
            <span className="my-score-name">{playerName}</span>
            <span className="my-score-total">{myTotalScore} pts</span>
          </div>
          <p className="subtitle">Check the main screen for the word cloud</p>

          <div className="game-controls">
            <button onClick={handleRefreshGame} className="refresh-button">
              🔄 Refresh Game
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Final leaderboard
  if (gameState === 'finished' && questionResult) {
    const myRank = questionResult.leaderboard
      ? questionResult.leaderboard.findIndex(e => e.name === playerName) + 1
      : 0;
    return (
      <div className="game-play-container">
        <div className="game-play-card">
          <h1>🏆 Final Results</h1>

          {playerName && (
            <div className="my-score-banner">
              <span className="my-score-name">{playerName}</span>
              <span className="my-score-total">
                {myRank > 0 ? `#${myRank}` : ''}
                {myRank > 0 && myTotalScore > 0 ? ' · ' : ''}
                {myTotalScore > 0 ? `${myTotalScore} pts` : ''}
              </span>
            </div>
          )}

          <div className="leaderboard">
            {questionResult.leaderboard.map((entry, index) => (
              <div key={index} className={`leaderboard-entry rank-${index + 1}`}>
                <span className="rank">{index + 1}</span>
                <span className="player-name">{entry.name}</span>
                <span className="score">{entry.totalScore} pts</span>
              </div>
            ))}
          </div>

          <div className="game-controls">
            <button onClick={handleRefreshGame} className="refresh-button">
              🔄 Refresh Game
            </button>
          </div>

          <div className="game-over-message">
            Thank you for playing!
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="game-play-container">
      <div className="game-play-card">
        <h1>Loading...</h1>
      </div>
    </div>
  );
}

export default GamePlay;
